import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface ActionMenuItem {
  id: string;
  label: string;
  /** Called when the item is chosen. Ignored for `href` items. */
  onSelect?: () => void;
  /** Navigates with a plain anchor. Router links belong in app wrappers. */
  href?: string;
  disabled?: boolean;
  busy?: boolean;
  busyLabel?: string;
  title?: string;
  ariaLabel?: string;
  /** Optional leading icon or glyph. */
  icon?: ReactNode;
  /** Optional trailing content such as a shortcut or count. */
  trailing?: ReactNode;
}

export interface ActionMenuProps {
  /** Visible trigger text, e.g. "Actions" or "Bulk Actions". */
  label: string;
  items: readonly ActionMenuItem[];
  /** Accessible trigger and menu name. Defaults to `label`. */
  ariaLabel?: string;
  align?: "start" | "end";
}

interface MenuPosition {
  top: number;
  left: number;
}

const MENU_WIDTH = 208;

function ItemContent({ item }: { item: ActionMenuItem }) {
  return (
    <>
      {item.icon ? (
        <span aria-hidden="true" className="shrink-0">
          {item.icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 text-left">
        {item.busy ? (item.busyLabel ?? "Working…") : item.label}
      </span>
      {item.trailing ? (
        <span className="shrink-0">{item.trailing}</span>
      ) : null}
    </>
  );
}

/** One dropdown for a table row or a bulk action group. The menu renders in
 * a portal so table overflow cannot clip it. Callers own the item set; the
 * table contract is that row actions live only here. */
export function ActionMenu({
  label,
  items,
  ariaLabel = label,
  align = "end",
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);
    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const preferredLeft =
      align === "end" ? rect.right - MENU_WIDTH : rect.left;
    const left = Math.max(
      8,
      Math.min(preferredLeft, window.innerWidth - MENU_WIDTH - 8),
    );
    setPosition({ top: rect.bottom + 4, left });
    setOpen(true);
  };

  const select = (item: ActionMenuItem) => {
    if (item.disabled || item.busy) return;
    setOpen(false);
    item.onSelect?.();
  };

  return (
    <>
      <span ref={buttonRef} className="inline-flex">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={toggle}
        >
          {label}
        </Button>
      </span>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label={ariaLabel}
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                width: MENU_WIDTH,
              }}
              className="z-50 grid max-h-80 gap-1 overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--surface)] p-2 shadow-lg"
            >
              {items.map((item) =>
                item.href && !item.disabled && !item.busy ? (
                  <Button
                    key={item.id}
                    asChild
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                  >
                    <a
                      role="menuitem"
                      href={item.href}
                      title={item.title}
                      aria-label={item.ariaLabel}
                      onClick={() => setOpen(false)}
                    >
                      <ItemContent item={item} />
                    </a>
                  </Button>
                ) : (
                  <Button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                    disabled={item.disabled || item.busy}
                    title={item.title}
                    aria-label={item.ariaLabel}
                    onClick={() => select(item)}
                  >
                    <ItemContent item={item} />
                  </Button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
